import { useEffect } from "react";
import clsx from "clsx";
import { useMineStore } from "../store/useMineStore";
import { useGameStore } from "../store/useGameStore";
import { FormattedNumber } from "../components/FormattedNumber";

const TABS = [
  { id: "mine" as const, label: "Mine" },
  { id: "shop" as const, label: "Merchant" },
];

const CLASS_ICON: Record<string, string> = {
  WARRIOR: "🛡️",
  ARCHER: "🏹",
  MAGE: "🔮",
};

const SLOT_LABELS = ["North Vein", "East Vein", "South Vein", "West Vein"];

function slotStateLabel(state: string) {
  if (state === "mining") return "Mining";
  if (state === "pvp") return "Battle";
  return "Idle";
}

function phaseLabel(phase: string) {
  switch (phase) {
    case "traveling":
      return "Moving…";
    case "mining":
      return "Mining";
    case "ready_to_claim":
      return "Ready";
    case "pvp_pending":
      return "PvP offer";
    case "pvp_fighting":
      return "Fighting";
    default:
      return "Idle";
  }
}

export function MineScreen() {
  const { hero, setScreen } = useGameStore();
  const {
    status,
    shop,
    tab,
    loading,
    actionLoading,
    error,
    message,
    setTab,
    refresh,
    refreshShop,
    enter,
    selectSlot,
    claim,
    deliver,
    exit,
    acceptPvp,
    buyShopItem,
  } = useMineStore();

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (tab === "shop") void refreshShop();
  }, [tab, refreshShop]);

  const unlocked = status?.unlocked ?? (hero?.level ?? 0) >= (status?.unlock_level ?? 10);
  const player = status?.player;
  const inMine = player?.is_in_mine ?? false;
  const amethyst = player?.amethyst_balance ?? shop?.amethyst ?? 0;

  return (
    <div className="p-4 space-y-4 pb-8">
      <button type="button" onClick={() => setScreen("main")} className="text-sm text-gray-400 hover:text-white">
        ← Back to Home
      </button>

      <div className="text-center space-y-1">
        <h2 className="text-xl font-bold font-game text-violet-300">🟣 Amethyst Mine</h2>
        <p className="text-gray-400 text-sm">
          Amethyst: <span className="text-violet-200 font-bold"><FormattedNumber value={amethyst} /></span>
          {status?.daily.event_active && (
            <span className="ml-2 text-amber-300 text-xs font-bold">• Event x2 ore</span>
          )}
        </p>
      </div>

      {!unlocked && (
        <div className="bg-kingdom-purple border border-kingdom-mid rounded-xl p-4 text-center">
          <p className="text-gray-300 text-sm">Unlocks at hero level {status?.unlock_level ?? 10}</p>
        </div>
      )}

      {unlocked && (
        <>
          <div className="flex gap-1 bg-kingdom-dark rounded-xl p-1 border border-kingdom-mid">
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className={clsx(
                  "flex-1 py-2 rounded-lg text-sm font-bold transition-colors",
                  tab === t.id ? "bg-violet-700 text-white" : "text-gray-400 hover:text-white",
                )}
              >
                {t.label}
              </button>
            ))}
          </div>

          {error && (
            <div className="bg-rose-900/30 border border-rose-500 rounded-xl p-3 text-rose-300 text-sm">{error}</div>
          )}
          {message && (
            <div className="bg-emerald-900/30 border border-emerald-500 rounded-xl p-3 text-emerald-300 text-sm">{message}</div>
          )}

          {tab === "mine" && (
            <MinePanel
              loading={loading}
              actionLoading={actionLoading}
              status={status}
              heroClass={hero?.hero_class ?? "WARRIOR"}
              inMine={inMine}
              onEnter={() => void enter()}
              onSelectSlot={(id) => void selectSlot(id)}
              onClaim={() => void claim()}
              onDeliver={() => void deliver()}
              onExit={() => void exit()}
              onAcceptPvp={(id) => void acceptPvp(id)}
            />
          )}

          {tab === "shop" && (
            <ShopPanel
              shop={shop}
              actionLoading={actionLoading}
              onBuy={(code) => void buyShopItem(code)}
            />
          )}
        </>
      )}
    </div>
  );
}

function MinePanel({
  loading,
  actionLoading,
  status,
  heroClass,
  inMine,
  onEnter,
  onSelectSlot,
  onClaim,
  onDeliver,
  onExit,
  onAcceptPvp,
}: {
  loading: boolean;
  actionLoading: boolean;
  status: ReturnType<typeof useMineStore.getState>["status"];
  heroClass: string;
  inMine: boolean;
  onEnter: () => void;
  onSelectSlot: (id: number) => void;
  onClaim: () => void;
  onDeliver: () => void;
  onExit: () => void;
  onAcceptPvp: (id: string) => void;
}) {
  const player = status?.player;
  const slots = status?.slots ?? [];
  const daily = status?.daily;

  if (loading && !status) {
    return <div className="text-center py-12 text-gray-400">Loading mine…</div>;
  }

  return (
    <div className="space-y-4">
      {daily && (
        <div className="bg-kingdom-dark border border-kingdom-mid rounded-xl p-3 text-xs text-gray-400 flex justify-between">
          <span>Runs today: {daily.attempts_used}/{daily.attempts_max}</span>
          <span>Ore today: {daily.ore_earned_today}/{daily.ore_cap_today}</span>
          <span>+{daily.ore_per_run}/run</span>
        </div>
      )}

      {/* Top — mining points */}
      <div className="grid grid-cols-2 gap-2">
        {slots.map((slot) => {
          const selected = player?.current_slot_id === slot.slot_id;
          const canPick = player?.can_select_slot && !actionLoading;
          return (
            <button
              key={slot.slot_id}
              type="button"
              disabled={!canPick || !inMine}
              onClick={() => onSelectSlot(slot.slot_id)}
              className={clsx(
                "rounded-xl border p-3 text-left transition-all",
                selected ? "border-violet-400 bg-violet-950/50 ring-1 ring-violet-400/60" : "border-kingdom-mid bg-kingdom-purple",
                canPick && inMine && "active:scale-95 hover:border-violet-500/60",
                (!canPick || !inMine) && "opacity-70",
              )}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-bold text-violet-200">{SLOT_LABELS[slot.slot_id - 1] ?? `Point ${slot.slot_id}`}</span>
                <span
                  className={clsx(
                    "text-[10px] font-bold uppercase px-1.5 py-0.5 rounded",
                    slot.state === "pvp" && "bg-rose-900/60 text-rose-200",
                    slot.state === "mining" && "bg-amber-900/60 text-amber-200",
                    slot.state === "idle" && "bg-gray-800 text-gray-400",
                  )}
                >
                  {slotStateLabel(slot.state)}
                </span>
              </div>
              <div className="mt-1 text-[10px] text-gray-500">{slot.player_count} miner{slot.player_count === 1 ? "" : "s"}</div>
            </button>
          );
        })}
      </div>

      {/* Center — mine visual */}
      <div className="relative bg-gradient-to-b from-violet-950/80 to-kingdom-dark border border-violet-900/50 rounded-2xl p-4 min-h-[180px] overflow-hidden">
        <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_50%_20%,#a78bfa_0%,transparent_55%)]" />
        <div className="relative z-10 flex flex-col items-center justify-center gap-3 py-4">
          <div className={clsx("text-6xl", player?.phase === "mining" && "animate-bounce")}>⛏️</div>
          <p className="text-sm text-violet-200 font-bold">{phaseLabel(player?.phase ?? "idle")}</p>
          {player?.phase === "traveling" && (
            <p className="text-xs text-gray-400">Arriving in {player.travel_seconds_left}s…</p>
          )}
          {player?.phase === "mining" && (
            <div className="w-full max-w-xs space-y-1">
              <div className="h-2 rounded-full bg-kingdom-dark overflow-hidden border border-violet-800">
                <div
                  className="h-full bg-gradient-to-r from-violet-500 to-fuchsia-400 transition-all duration-1000"
                  style={{ width: `${player.mining_progress_pct}%` }}
                />
              </div>
              <p className="text-[10px] text-center text-gray-400">{player.mining_seconds_left}s left</p>
            </div>
          )}
          {player && player.current_ore > 0 && (
            <p className="text-xs text-amber-300 font-bold">🎒 {player.current_ore} ore on you — deliver to merchant!</p>
          )}
        </div>
      </div>

      {/* Bottom — miners + actions */}
      <div className="bg-kingdom-purple border border-kingdom-mid rounded-xl p-4 space-y-3">
        <div className="text-xs text-gray-400 font-bold uppercase tracking-wide">Miners at your point</div>
        <div className="flex flex-wrap gap-2 min-h-[40px]">
          {(slots.find((s) => s.slot_id === player?.current_slot_id)?.players ?? []).map((p) => (
            <div
              key={p.user_id}
              className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-kingdom-dark border border-kingdom-mid text-xs"
            >
              <span>{CLASS_ICON[p.hero_class] ?? "⛏️"}</span>
              <span className="text-gray-200 truncate max-w-[80px]">{p.display_name}</span>
            </div>
          ))}
          {!(player?.current_slot_id && slots.find((s) => s.slot_id === player.current_slot_id)?.players.length) && (
            <span className="text-gray-500 text-sm">No miners here yet</span>
          )}
        </div>

        <div className="flex items-center gap-2 px-2 py-2 rounded-lg bg-violet-950/40 border border-violet-800/50">
          <span className="text-xl">{CLASS_ICON[heroClass] ?? "🧙"}</span>
          <div className="text-xs">
            <div className="text-violet-200 font-bold">You</div>
            <div className="text-gray-500">{phaseLabel(player?.phase ?? "idle")}</div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-2">
          {!inMine && (
            <button
              type="button"
              disabled={actionLoading}
              onClick={onEnter}
              className="w-full py-3 rounded-xl bg-violet-700 text-white font-bold disabled:opacity-40 active:scale-95"
            >
              Enter Mine
            </button>
          )}
          {inMine && player?.can_select_slot && (
            <p className="text-center text-xs text-gray-500">Tap a mining point above to start a run</p>
          )}
          {player?.can_claim && (
            <button
              type="button"
              disabled={actionLoading}
              onClick={onClaim}
              className="w-full py-3 rounded-xl bg-amber-700 text-white font-bold disabled:opacity-40 active:scale-95"
            >
              Collect {player.pending_reward} ore
            </button>
          )}
          {player?.can_deliver && (
            <button
              type="button"
              disabled={actionLoading}
              onClick={onDeliver}
              className="w-full py-3 rounded-xl bg-emerald-700 text-white font-bold disabled:opacity-40 active:scale-95"
            >
              Deliver {player.current_ore} ore to merchant
            </button>
          )}
          {inMine && player?.can_exit && (
            <button
              type="button"
              disabled={actionLoading}
              onClick={onExit}
              className="w-full py-2.5 rounded-xl border border-gray-600 text-gray-300 text-sm font-bold disabled:opacity-40"
            >
              Leave mine
            </button>
          )}
        </div>
      </div>

      {player?.pvp && (
        <div className="fixed inset-0 z-50 bg-black/75 flex items-end sm:items-center justify-center p-4">
          <div className="w-full max-w-sm bg-kingdom-purple border border-rose-500/60 rounded-2xl p-5 space-y-4 shadow-2xl">
            <div className="text-center space-y-1">
              <div className="text-3xl">⚔️</div>
              <h3 className="text-lg font-bold text-rose-300">Mine Duel!</h3>
              <p className="text-sm text-gray-300">
                {player.pvp.opponent_name} ({player.pvp.opponent_class}) is at the same vein.
              </p>
              <p className="text-xs text-gray-500">Accept to fight for both players&apos; ore. Cannot cancel once started.</p>
            </div>
            <button
              type="button"
              disabled={actionLoading}
              onClick={() => onAcceptPvp(player.pvp!.encounter_id)}
              className="w-full py-3 rounded-xl bg-rose-700 text-white font-bold disabled:opacity-40 active:scale-95"
            >
              Accept Battle
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ShopPanel({
  shop,
  actionLoading,
  onBuy,
}: {
  shop: ReturnType<typeof useMineStore.getState>["shop"];
  actionLoading: boolean;
  onBuy: (code: string) => void;
}) {
  if (!shop) {
    return <div className="text-center py-12 text-gray-400">Loading merchant…</div>;
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-400 text-center">
        Balance: <span className="text-violet-200 font-bold"><FormattedNumber value={shop.amethyst} /></span> amethyst
      </p>
      {shop.items.map((item) => (
        <div key={item.code} className="bg-kingdom-purple border border-kingdom-mid rounded-xl p-4 space-y-2">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="font-bold text-violet-200">{item.name}</div>
              <div className="text-xs text-gray-500 mt-1">{item.description}</div>
            </div>
            <div className="text-right text-xs shrink-0">
              <div className="text-violet-300 font-bold">{item.amethyst_cost} 💎</div>
              {item.gold_reward > 0 && <div className="text-amber-300">+{item.gold_reward.toLocaleString("en-US")} gold</div>}
            </div>
          </div>
          <button
            type="button"
            disabled={actionLoading || shop.amethyst < item.amethyst_cost}
            onClick={() => onBuy(item.code)}
            className="w-full py-2.5 rounded-xl bg-violet-800 text-white text-sm font-bold disabled:opacity-40 active:scale-95"
          >
            Exchange
          </button>
        </div>
      ))}
      <p className="text-[10px] text-gray-500 text-center">More mine shop items coming soon.</p>
    </div>
  );
}
